import { request } from './api.js';
import type { IpointTrendParams, IpointTrendResponse } from '../types/api.js';

/** @graph-connects none */
export interface BugEvaluationListItem {
  issueKey: string;
  evaluatedAt: string;
  evaluator: string;
  bugType: string;
  devCauseType: string;
  negligenceLevel: string;
  iPoint: number;
  isHumanReviewed: boolean;
  summary: string;
  responsibleTeam: string;
  causeProjectKey: string;
  causeProjectSummary: string;
  causeProjectDevLeader: string;
  devLeaderGroup: string;
  devLeaderTeam: string;
}

/** @graph-connects none */
export interface BugEvaluationDetail {
  issueKey: string;
  evaluatedAt: string;
  evaluator: string;
  evaluationDate: string;
  bugType: string;
  devCauseType: string;
  improvementPhaseDev: string;
  testCauseType: string;
  improvementPhaseTest: string;
  improvementPhaseReview: string;
  recurrence: string;
  antiPattern: string;
  irregular: string;
  devFlowCompliance: string;
  negligenceLevel: string;
  iPoint: number;
  evaluationReason: string;
  modelUsed: string;
  isHumanReviewed: boolean;
  reviewedBy: string | null;
  /** 起因案件の実装担当者（ニックネーム。未確定は null） */
  implementer?: string | null;
  /** 実装担当者に選べる在籍者のニックネーム。画面はここから選ばせる＝手入力させない */
  implementerOptions?: string[];
  reviewedAt: string | null;
  issueSummary: string;
  incidentLevel: string;
  classification: string;
  service: string;
  issueCause: string;
  causeProjectKey: string;
  bugSummary: string;
  expectedResult: string;
  actualResult: string;
  bugReason: string;
  stepsToReproduce: string;
  workaroundPlan: string;
  permanentSolution: string;
  relatedIssueKey: string;
  sourceLocale?: 'ja' | 'en';
  isTranslated?: boolean;
}

/** @graph-connects none */
export interface BugEvaluationReviewInput {
  bugType?: string;
  devCauseType?: string;
  improvementPhaseDev?: string;
  testCauseType?: string;
  improvementPhaseTest?: string;
  improvementPhaseReview?: string;
  recurrence?: string;
  antiPattern?: string;
  irregular?: string;
  devFlowCompliance?: string;
  negligenceLevel?: string;
  iPoint?: number;
  evaluationReason?: string;
  locale?: 'ja' | 'en';
  /** 起因案件の実装担当者（ニックネーム）。空文字で未確定へ戻す */
  implementer?: string;
}

/**
 * 実装担当者の候補の根拠 1 件（どのリポジトリのどの PR か）。
 *
 * `url` は API が組み立てて返す。organization 名を画面に写さないため、ここでは組み立てない。
 * @graph-connects none
 */
export interface ImplementerCandidateEvidence {
  repo: string;
  number: number;
  headRef: string;
  title: string;
  url: string | null;
}

/**
 * 実装担当者の候補 1 人分。
 *
 * `nickName` が null の候補は在籍者に紐づかない（退職者等）ため**選べない**。
 * @graph-connects none
 */
export interface ImplementerCandidate {
  nickName: string | null;
  githubLogin: string;
  prCount: number;
  source: 'branch' | 'title' | 'commit';
  evidence: ImplementerCandidateEvidence[];
}

/**
 * 起因案件の付け替え先候補 1 件。
 *
 * **氏名を持たない。** 選ぶ基準は「そのコードを作った PR がどのチャンクのものか」で、
 * 誰に点が付くかではない。`prCount` が 0 のチャンクはそのコードを作っていないので候補から
 * 外せる。`repositories` が起因リポジトリと一致するかが一番効く絞り込み。
 * @graph-connects none
 */
export interface CauseProjectCandidate {
  issueKey: string;
  summary: string | null;
  issueTypeName: string | null;
  releasedAt: string | null;
  prCount: number;
  repositories: string[];
  pullRequests: CauseProjectPullRequest[];
}

/** 候補のチャンクに紐づくマージ済み PR 1 件。 @graph-connects none */
export interface CauseProjectPullRequest {
  repository: string;
  number: number;
  title: string | null;
  mergedAt: string | null;
  /** PR の URL。API が組み立てて返す（organization 名を画面に写さない）。 */
  url: string | null;
}

/**
 * 起因案件の現状と付け替え先候補。
 *
 * `rejection` が null なら今の起因案件は i-point の帰属先を持てる種別で、付け替えは不要。
 * @graph-connects none
 */
export interface CauseProjectCandidates {
  current: CauseProjectCandidate | null;
  rejection: { code: string; message: string } | null;
  candidates: CauseProjectCandidate[];
  /**
   * 統合プロダクトのキー自体に紐づくマージ済み PR の件数。
   *
   * どの候補にも PR が無いときの理由を区別するために使う（0 なら未着手、1 以上なら
   * PR が統合プロダクトのキーで記録されていてチャンクへ辿れない）。
   */
  integratedProductPrCount: number;
}

/** 起因案件の付け替えの入力。 @graph-connects none */
export interface CauseProjectUpdateInput {
  causeProjectKey: string;
  note?: string;
}

/** 実装担当者の候補一式（まだ引いていなければ null）。 @graph-connects none */
export interface ImplementerCandidates {
  issueKey: string;
  causeKey: string;
  candidates: ImplementerCandidate[];
  submittedBy: string;
  submittedAt: string;
}

/**
 * AI 一次判定が見送った 1 件。
 *
 * **見送りは評価本体を持たない**ので、評価一覧（`getList`）には現れない。`declineReason` が
 * 「なぜ今は点を付けられないか」で、多くはチケット側の未確定（起因案件が暫定・原因欄が
 * 未確認・原因分類が未設定）を指している。
 * @graph-connects none
 */
export interface BugEvaluationAiDecline {
  issueKey: string;
  declineReason: string | null;
  modelUsed: string | null;
  submittedBy: string;
  submittedAt: string;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価APIクライアント。一覧取得・詳細取得・実装担当者の候補取得・レビュー更新・i-point月次推移取得を提供
 * @graph-connects /bug-evaluations [calls] バグ評価一覧・詳細・実装担当者の候補・レビュー更新APIエンドポイント
 * @graph-connects /bug-evaluations/ai-drafts [calls] 一次判定が見送った課題の一覧APIエンドポイント
 * @graph-connects /bug-evaluations/ipoint-trend [calls] i-point月次推移APIエンドポイント
 */
export const bugEvaluationApi = {
  getList: async (params?: {
    startDate?: string;
    endDate?: string;
    reviewStatus?: string;
    team?: string;
    locale?: string;
    /** 期間フィルタの基準日。releasedAt=起因案件リリース日、createdAt=バグ起票日、evaluatedAt=評価日時（デフォルト） */
    dateBasis?: 'evaluatedAt' | 'releasedAt' | 'createdAt';
  }): Promise<{ data: BugEvaluationListItem[]; total: number }> => {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.reviewStatus) query.set('reviewStatus', params.reviewStatus);
    if (params?.team) query.set('team', params.team);
    if (params?.locale) query.set('locale', params.locale);
    if (params?.dateBasis) query.set('dateBasis', params.dateBasis);
    const qs = query.toString();
    return request<{ data: BugEvaluationListItem[]; total: number }>(
      `/bug-evaluations${qs ? `?${qs}` : ''}`,
    );
  },
  getDetail: async (issueKey: string, locale?: string): Promise<{ data: BugEvaluationDetail }> => {
    const query = locale ? `?locale=${locale}` : '';
    return request<{ data: BugEvaluationDetail }>(
      `/bug-evaluations/${encodeURIComponent(issueKey)}${query}`,
    );
  },
  updateReview: async (
    issueKey: string,
    data: BugEvaluationReviewInput,
  ): Promise<{ success: boolean; data: BugEvaluationDetail }> => {
    return request<{ success: boolean; data: BugEvaluationDetail }>(
      `/bug-evaluations/${encodeURIComponent(issueKey)}`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  },
  getAiDrafts: async (): Promise<{ data: BugEvaluationAiDecline[]; total: number }> => {
    return request<{ data: BugEvaluationAiDecline[]; total: number }>('/bug-evaluations/ai-drafts');
  },
  getImplementerCandidates: async (
    issueKey: string,
  ): Promise<{ data: ImplementerCandidates | null }> => {
    return request<{ data: ImplementerCandidates | null }>(
      `/bug-evaluations/${encodeURIComponent(issueKey)}/implementer-candidates`,
    );
  },
  getCauseProjectCandidates: async (
    issueKey: string,
  ): Promise<{ data: CauseProjectCandidates }> => {
    return request<{ data: CauseProjectCandidates }>(
      `/bug-evaluations/${encodeURIComponent(issueKey)}/cause-project/candidates`,
    );
  },
  updateCauseProject: async (
    issueKey: string,
    data: CauseProjectUpdateInput,
  ): Promise<{ success: boolean }> => {
    return request<{ success: boolean }>(
      `/bug-evaluations/${encodeURIComponent(issueKey)}/cause-project`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  },
  getMy: async (params?: {
    locale?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ data: BugEvaluationListItem[]; total: number }> => {
    const query = new URLSearchParams();
    if (params?.locale) query.set('locale', params.locale);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    const qs = query.toString();
    return request<{ data: BugEvaluationListItem[]; total: number }>(
      `/bug-evaluations/my${qs ? `?${qs}` : ''}`,
    );
  },
  getIpointTrend: async (params?: IpointTrendParams): Promise<IpointTrendResponse> => {
    const q = new URLSearchParams();
    if (params?.team) q.set('team', params.team);
    if (params?.months !== undefined) q.set('months', String(params.months));
    if (params?.dateBasis) q.set('dateBasis', params.dateBasis);
    const qs = q.toString();
    return request<IpointTrendResponse>(`/bug-evaluations/ipoint-trend${qs ? `?${qs}` : ''}`);
  },
};
